import React, { useState, useEffect } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

function ProgressTrack({ gift, onToggle, readOnly = false }) {
  const steps = ["bought", "shipped", "received", "wrapped"];
  const labels = ["Bought", "Shipped", "Received", "Wrapped"];
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
      {steps.map((step, i) => (
        <div key={step} onClick={readOnly ? undefined : e => { e.stopPropagation(); onToggle(gift.id, step, !gift[step]); }} style={{ flex: 1, cursor: readOnly ? "default" : "pointer" }}>
          <div style={{ width: "100%", height: 8, borderRadius: 4, background: gift[step] ? "var(--sage)" : "var(--border)", transition: "background 0.15s" }} />
          <div style={{ fontSize: 10, color: gift[step] ? "var(--sage)" : "var(--muted)", fontWeight: gift[step] ? 600 : 400, marginTop: 3, textAlign: "center" }}>{labels[i]}</div>
        </div>
      ))}
    </div>
  );
}

function GiftCard({ gift, onToggle, readOnly = false }) {
  return (
    <div style={{ background: "var(--white)", borderRadius: 10, padding: "11px 13px", border: "1px solid var(--border)", marginBottom: 7 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "var(--charcoal)" }}>{gift.name || <span style={{ color: "var(--muted)", fontStyle: "italic" }}>Unnamed gift</span>}</div>
          {gift.note && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{gift.note}</div>}
        </div>
        {gift.stocking && <span style={{ fontSize: 11 }}>🧦</span>}
      </div>
      <ProgressTrack gift={gift} onToggle={onToggle} readOnly={readOnly} />
    </div>
  );
}

function OccasionSection({ occasion, gifts, onToggle, readOnly = false }) {
  const [open, setOpen] = useState(true);
  const bought = gifts.filter(g => g.bought).length;
  return (
    <div style={{ marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--charcoal)" }}>{occasion}</span>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>{bought}/{gifts.length} bought</span>
        </div>
        <span style={{ color: "var(--muted)", fontSize: 13, display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && gifts.map(g => <GiftCard key={g.id} gift={g} onToggle={onToggle} readOnly={readOnly} />)}
    </div>
  );
}

function PersonSection({ person, gifts, onToggle, readOnly = false }) {
  const [open, setOpen] = useState(true);
  const occasions = [...new Set(gifts.map(g => g.occasion).filter(Boolean))];
  return (
    <div style={{ marginBottom: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "var(--white)", borderRadius: open ? "12px 12px 0 0" : 12, border: "1px solid var(--border)", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--charcoal)" }}>{person.name}</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>{gifts.length} gift{gifts.length !== 1 ? "s" : ""}</span>
        </div>
        <span style={{ color: "var(--muted)", display: "inline-block", transform: open ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>⌄</span>
      </div>
      {open && (
        <div style={{ padding: "10px 14px 4px", background: "var(--white)", border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px" }}>
          {occasions.length === 0 && <div style={{ padding: "10px 0", fontSize: 13, color: "var(--muted)" }}>No gifts yet</div>}
          {occasions.map(occ => (
            <OccasionSection key={occ} occasion={occ} gifts={gifts.filter(g => g.occasion === occ)} onToggle={onToggle} readOnly={readOnly} />
          ))}
        </div>
      )}
    </div>
  );
}

function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");
    try {
      const res = await fetch(`/.netlify/functions/notion?type=kidsGiftPeople&pw=${encodeURIComponent(pw)}`);
      if (res.status === 401) { setError("Incorrect password."); setChecking(false); return; }
      if (!res.ok) { setError("Something went wrong. Try again."); setChecking(false); return; }
      const data = await res.json();
      onUnlock(data.items || []);
    } catch {
      setError("Something went wrong. Try again.");
      setChecking(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "70vh", padding: "0 24px" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, fontWeight: 600, color: "var(--charcoal)", marginBottom: 6, textAlign: "center" }}>Kids' Gifts</div>
      <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20, textAlign: "center" }}>Enter the password to view</div>
      <form onSubmit={submit} style={{ width: "100%", maxWidth: 280 }}>
        <input
          type="password"
          value={pw}
          onChange={e => setPw(e.target.value)}
          placeholder="Password"
          autoFocus
          style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--border)", fontSize: 15, fontFamily: "inherit", marginBottom: 10, boxSizing: "border-box" }}
        />
        {error && <div style={{ fontSize: 12, color: "var(--rose)", marginBottom: 10, textAlign: "center" }}>{error}</div>}
        <button type="submit" disabled={checking || !pw} style={{ width: "100%", padding: "11px 0", borderRadius: 10, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 14, cursor: checking || !pw ? "default" : "pointer", opacity: checking || !pw ? 0.6 : 1, fontFamily: "inherit" }}>
          {checking ? "Checking…" : "Unlock"}
        </button>
      </form>
    </div>
  );
}

export default function KidsGiftsPage({ onBack, onTabTap, readOnly = false }) {
  const [unlocked, setUnlocked] = useState(false);
  const [people, setPeople] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    fetch("/.netlify/functions/notion?type=gifts")
      .then(r => r.json())
      .then(d => setGifts(d.items || []))
      .finally(() => setLoading(false));
  }, [unlocked]);

  const handleUnlock = (kidPeople) => {
    setPeople(kidPeople);
    setUnlocked(true);
  };

  const handleToggle = async (giftId, field, val) => {
    setGifts(prev => prev.map(g => g.id === giftId ? { ...g, [field]: val } : g));
    await fetch("/.netlify/functions/notion", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: giftId, action: "updateGiftProgress", value: { [field]: val } }),
    });
  };

  return (
    <div className="app">
      <header style={{ background: "var(--white)", padding: "54px 20px 14px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>🔒 Kids' Gifts</span>
      </header>

      <div className="scroll-body" style={{ padding: "12px 16px", paddingBottom: 80 }}>
        {!unlocked ? (
          <PasswordGate onUnlock={handleUnlock} />
        ) : loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>
        ) : (
          people.map(p => (
            <PersonSection key={p.id} person={p} gifts={gifts.filter(g => g.recipientIds.includes(p.id))} onToggle={handleToggle} readOnly={readOnly} />
          ))
        )}
      </div>

      <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
        {NAV_TABS.map(t => (
          <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
            <div style={{ fontSize: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
          </div>
        ))}
      </nav>
    </div>
  );
}
